import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { type AgentSearchResult } from "../AdminService";

/**
 * Upper bound on ids resolved per render, matching the admin agent-search
 * endpoint's ``MAX_ADMIN_SEARCH_LIMIT`` (also the cap on the ``limit`` this
 * hook derives from the id count). The id list is capped and the hook reports
 * ``truncated`` when ids were dropped. Unreachable at today's page sizes
 * (≤ 100), but a page-size bump must not silently break the column.
 */
const MAX_ADMIN_AGENT_IDS = 200;

/**
 * Cross-page-reuse freshness window: agent identity (name / org) changes
 * rarely enough that a resolution made for one thread page can serve later
 * pages for several minutes without a refetch.
 */
const STALE_TIME_MS = 5 * 60 * 1000;

/** Sort + de-duplicate so a set of ids maps to one canonical, order-stable form. */
function normalizeAgentIds(agentIds: string[]): string[] {
  return Array.from(new Set(agentIds)).sort();
}

/**
 * Cache key for one agent's admin lookup. Per-id (rather than per page-id-set)
 * so consecutive thread pages — which overlap heavily in agents — share cache
 * entries: a page turn only fetches agents not already resolved, and the
 * entry count is bounded by the agent population, not by pages visited.
 * Exported for prefetch / invalidation / test assertions, and so this hook's
 * own batch query can seed each id's entry after a shared fetch.
 */
export function agentQueryKey(agentId: string) {
  return ["adminAgent", agentId] as const;
}

/** Cache key for this hook's own batch query over one normalized id set. */
function batchQueryKey(ids: string[]) {
  return ["adminAgentsByIds", ids] as const;
}

/**
 * Resolve a bounded set of agents by id, cross-org, via one batched request
 * per set of not-yet-cached ids.
 *
 * Backs the admin threads table's Agent-name column: the table collects the
 * distinct ``created_from_agent_id`` values of the *current page* and hands
 * them here. The ids are normalized (deduped, sorted, capped at
 * {@link MAX_ADMIN_AGENT_IDS}) and resolved by a single ``useQuery`` whose
 * ``queryFn``:
 *
 *  1. checks the query cache (under {@link agentQueryKey}) for each id and
 *     collects the hits — ids resolved on a previous page cost nothing;
 *  2. fetches every remaining ("missing") id in one
 *     ``adminService.searchAdminAgents`` call, expressed as an OR group of
 *     EQUALS conditions on ``agent_id``;
 *  3. seeds each fetched id's own {@link agentQueryKey} cache entry —
 *     including an explicit ``null`` for an id the endpoint didn't return —
 *     so a later page that needs the same id is a cache hit even though this
 *     hook itself never issued a per-id query; and
 *  4. returns the combined ``id -> agent | null`` object.
 *
 * The queryFn returns a plain object rather than a ``Map``: TanStack 5.28.8's
 * structural sharing (``replaceEqualDeep``) only recurses into plain objects
 * and arrays, not ``Map``, so a ``Map`` result is a fresh reference on every
 * render even when its contents haven't changed. A plain object lets
 * structural sharing keep the same reference across renders whenever the
 * resolved set is unchanged, which is what lets the table's
 * ``refreshCells`` effect (keyed on this hook's output) skip firing on
 * renders where nothing actually resolved.
 *
 * An id still in flight is absent from the result — the three-way
 * distinction (pending / missing / resolved) is what lets a cell show a
 * skeleton instead of flashing the raw id on every page load. An empty id
 * list issues no request.
 */
export function useAdminAgentsByIds(agentIds: string[]): {
  /**
   * SETTLED lookups only: an id maps to its agent, or to ``null`` when the
   * endpoint couldn't resolve it. An id still in flight is ABSENT — the
   * three-way distinction (pending / missing / resolved) is what lets a cell
   * show a skeleton instead of flashing the raw id on every page load.
   */
  resolvedById: Record<string, AgentSearchResult | null>;
  /** True while the batch lookup is still in flight. */
  isLoading: boolean;
  /** True when the batch lookup failed (deserves the caller's error surface). */
  isError: boolean;
  /** True when more distinct ids were requested than one render can resolve. */
  truncated: boolean;
} {
  const { adminService } = useDomainServices();
  const queryClient = useQueryClient();

  const normalized = normalizeAgentIds(agentIds);
  const ids = normalized.slice(0, MAX_ADMIN_AGENT_IDS);

  const query = useQuery({
    queryKey: batchQueryKey(ids),
    queryFn: async ({
      signal,
    }: {
      signal: AbortSignal;
    }): Promise<Record<string, AgentSearchResult | null>> => {
      const resolvedById: Record<string, AgentSearchResult | null> = {};
      const missing: string[] = [];
      for (const id of ids) {
        const cached = queryClient.getQueryData<AgentSearchResult | null>(
          agentQueryKey(id),
        );
        if (cached !== undefined) {
          resolvedById[id] = cached;
        } else {
          missing.push(id);
        }
      }

      if (missing.length > 0) {
        // The `missing.length > 0` guard above doubles as the empty-set
        // gate: the backend rejects an empty OR group as invalid, so zero
        // missing ids must never reach the wire — "no ids" means "no
        // request", preserving the retired GET endpoint's empty-set
        // semantics (an empty id set resolves nothing).
        const page = await adminService.searchAdminAgents(
          {
            condition: {
              operator: "OR",
              conditions: missing.map((id) => ({
                field: "agent_id",
                comparator: "EQUALS" as const,
                value: id,
              })),
            },
            // An exact id set implies its own page size: asking for N
            // specific agents must return all N, whatever the endpoint's
            // default page size is. This invariant used to live server-side
            // (the GET endpoint derived its limit from the id count); the
            // structured POST contract moves it to the caller. Capped at
            // the endpoint's MAX_ADMIN_SEARCH_LIMIT.
            limit: Math.min(missing.length, MAX_ADMIN_AGENT_IDS),
          },
          { abortSignal: signal },
        );
        const foundById = new Map(
          page.items.map((agent) => [agent.agent_id, agent] as const),
        );
        for (const id of missing) {
          const agent = foundById.get(id) ?? null;
          resolvedById[id] = agent;
          // Seed each id's own cache entry so a later page needing the same
          // id — even under a different normalized id set — is a cache hit
          // rather than re-fetching. The explicit `null` for an unreturned
          // id caches the miss too.
          queryClient.setQueryData(agentQueryKey(id), agent);
        }
      }

      return resolvedById;
    },
    enabled: ids.length > 0,
    staleTime: STALE_TIME_MS,
  });

  return {
    resolvedById: query.data ?? {},
    isLoading: ids.length > 0 && query.isPending,
    isError: query.isError,
    truncated: normalized.length > MAX_ADMIN_AGENT_IDS,
  };
}
